import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * Reset password page. Reads the token from ?token= in the URL, sends the
 * new password to the backend, and redirects to /login on success.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) { setErr("Password must be at least 6 characters."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      navigate("/login?reset=1", { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Reset failed. Request a new link.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="reset-page" className="min-h-screen bg-espresso text-ivory flex items-center">
      <div className="max-w-md mx-auto px-6 md:px-10 py-16 w-full">
        <Link to="/login" className="label-xs text-ivory/60 link-underline">← Back to sign in</Link>
        <div className="mt-8 label-xs text-gold/80">Reset password</div>
        <h1 className="mt-4 font-serif text-4xl leading-[0.95] tracking-tight text-ivory">
          Choose a new<br/>
          <span className="italic text-ivory/60 font-light">password.</span>
        </h1>
        {!token ? (
          <p className="mt-8 text-sm text-ivory/70">This link is missing its reset token. Please <Link to="/forgot-password" className="link-underline">request a new one</Link>.</p>
        ) : (
          <form onSubmit={submit} className="mt-10 space-y-3" data-testid="reset-form">
            <input
              type="password"
              placeholder="New password (6+ chars)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="reset-password-input"
              className="w-full bg-transparent border border-ivory/30 px-4 py-3 text-ivory placeholder:text-ivory/40 focus:outline-none focus:border-ivory rounded-sm"
            />
            {err && <p className="text-xs text-red-300" data-testid="reset-error">{err}</p>}
            <button
              type="submit"
              disabled={busy}
              data-testid="reset-submit"
              className="w-full px-8 py-4 bg-gold text-espresso rounded-full text-xs uppercase tracking-[0.24em] hover:bg-gold-hover disabled:opacity-40"
            >
              {busy ? "…" : "Update password"}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}
